import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLibraryOrg } from '@/context/LibraryOrgContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, CheckCircle2, Clock3, Download, MapPin, QrCode } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { requester_name: '', requester_email: '', requester_phone: '', loan_days: '14', notes: '' };

const EBOOK_FORMAT_META = {
  pdf: { label: 'PDF downloaden', description: 'Vaste boekopmaak · geschikt voor scherm en print' },
  epub: { label: 'EPUB 3 downloaden', description: 'Meebewegende tekst · geschikt voor e-readers en leesapps' },
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const SimilarBookCard = ({ book, orgSlug }) => (
  <Link to={`/${orgSlug}/bibliotheek/boek/${book.slug}`} className="bg-white rounded-xl border border-slate-100 p-3 hover:shadow-sm transition-shadow">
    {book.cover_image_url ? (
      <div className="aspect-[3/4] w-full rounded-lg overflow-hidden bg-slate-100 mb-2">
        <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
      </div>
    ) : (
      <div className="aspect-[3/4] w-full rounded-lg bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-2 mb-2">
        <span className="text-white text-center text-xs font-semibold leading-snug line-clamp-4">{book.title}</span>
      </div>
    )}
    <p className="text-sm font-medium text-slate-900 line-clamp-2">{book.title}</p>
    {book.author_name && <p className="text-xs text-slate-500">{book.author_name}</p>}
  </Link>
);

const LibraryPublicBookPage = ({ byId = false }) => {
  const org = useLibraryOrg();
  const { slug, bookId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    const url = byId
      ? `${API}/public/library/${org.id}/books/${bookId}`
      : `${API}/public/library/${org.id}/books/by-slug/${slug}`;
    axios.get(url)
      .then((res) => {
        setBook(res.data);
        if (byId) {
          navigate(`/${org.slug}/bibliotheek/boek/${res.data.slug}${searchParams.get('actie') ? '?actie=reserveren' : ''}`, { replace: true });
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id, slug, bookId, byId]);

  useEffect(() => {
    if (book && searchParams.get('actie') === 'reserveren' && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [book, searchParams]);

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!formData.requester_name || !formData.requester_email) {
      toast.error('Naam en e-mailadres zijn verplicht');
      return;
    }
    setSubmitting(true);
    try {
      const response = book.available
        ? await axios.post(`${API}/public/library/${org.id}/checkout`, {
          book_id: book.id,
          borrower_name: formData.requester_name,
          borrower_email: formData.requester_email,
          loan_days: Number(formData.loan_days),
          notes: formData.notes || null,
        })
        : await axios.post(`${API}/public/library/${org.id}/reservations`, {
          book_id: book.id,
          requester_name: formData.requester_name,
          requester_email: formData.requester_email,
          requester_phone: formData.requester_phone || null,
          notes: formData.notes || null,
        });
      setSubmitted(book.available ? { type: 'loan', ...response.data } : { type: 'reservation', ...response.data });
      setFormData(EMPTY_FORM);
      toast.success(book.available ? 'Boek is als uitgeleend geregistreerd' : 'Reservering verzonden');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aanvraag versturen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Boek laden...</p>
        </div>
      </div>
    );
  }

  if (notFound || !book) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center p-6">
        <div>
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Dit boek is niet (meer) beschikbaar in de publieke bibliotheek.</p>
        </div>
      </div>
    );
  }

  const similarBooks = book.similar_books || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Link to={`/${org.slug}/bibliotheek`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Terug naar de bibliotheek
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-8">
        <div className="grid sm:grid-cols-[240px_1fr] gap-6">
          {book.cover_image_url ? (
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-slate-100">
              <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center p-4">
              <span className="text-white text-center font-semibold leading-snug">{book.title}</span>
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{book.title}</h1>
            {book.subtitle && <p className="text-slate-500">{book.subtitle}</p>}
            {book.author_name && <p className="text-slate-600 mt-1">{book.author_name}</p>}

            <div className="flex flex-wrap gap-2 mt-3">
              {book.category_name && (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{book.category_name}</span>
              )}
              {book.is_digital ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">Digitaal e-book</span>
              ) : (
                <>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${book.available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                    {book.available ? 'Beschikbaar' : 'Uitgeleend'}
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                    {book.copy_available} van {book.copy_total} exemplaren beschikbaar
                  </span>
                </>
              )}
            </div>

            {book.location && (
              <p className="text-sm text-slate-500 mt-3 flex items-center gap-1"><MapPin className="w-4 h-4" /> {book.location}</p>
            )}

            {book.description && <p className="text-slate-600 mt-4 leading-relaxed">{book.description}</p>}

            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 text-sm">
              {book.isbn && <div><dt className="text-slate-400">ISBN</dt><dd className="text-slate-700">{book.isbn}</dd></div>}
              {book.language && <div><dt className="text-slate-400">Taal</dt><dd className="text-slate-700">{book.language}</dd></div>}
              {book.publish_year && <div><dt className="text-slate-400">Jaar</dt><dd className="text-slate-700">{book.publish_year}</dd></div>}
            </dl>
          </div>
        </div>

        {book.is_digital ? (
          <div ref={formRef} className="bg-white rounded-2xl shadow-sm p-6 max-w-lg">
            <div className="flex items-center gap-2"><Download className="h-5 w-5 text-teal-600" /><h2 className="text-lg font-semibold text-slate-900">Download dit e-book</h2></div>
            {book.has_ebook ? (
              <>
                <p className="text-sm text-slate-500 mt-1">Dit is een digitaal werk. Kies het formaat dat bij jouw apparaat past.</p>
                <div className="mt-4 space-y-3">
                  {(book.ebook_formats?.length ? book.ebook_formats : [{ format: null }]).map((item) => {
                    const meta = EBOOK_FORMAT_META[item.format] || { label: 'E-book downloaden', description: 'Digitaal boekbestand' };
                    const href = `${API}/public/library/${org.id}/books/${book.id}/ebook/download${item.format ? `?file_format=${encodeURIComponent(item.format)}` : ''}`;
                    return (
                      <a key={item.format || 'legacy'} href={href} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-teal-300 hover:bg-teal-50">
                        <span className="min-w-0"><span className="block text-sm font-semibold text-slate-900">{meta.label}</span><span className="mt-0.5 block text-xs text-slate-500">{meta.description}{formatFileSize(item.size_bytes) ? ` · ${formatFileSize(item.size_bytes)}` : ''}</span></span>
                        <Download className="h-5 w-5 shrink-0 text-teal-700" />
                      </a>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 mt-1">Het bestand is nog niet beschikbaar.</p>
            )}
          </div>
        ) : (
        <div ref={formRef} className="bg-white rounded-2xl shadow-sm p-6 max-w-lg">
          <div className="flex items-center gap-2"><QrCode className="h-5 w-5 text-teal-600" /><h2 className="text-lg font-semibold text-slate-900">{book.available ? 'Neem dit boek direct mee' : 'Reserveer dit boek'}</h2></div>
          <p className="text-sm text-slate-500 mt-1">
            {book.available
              ? 'Vul je naam, e-mailadres en leenperiode in. Na bevestiging staat het boek direct als uitgeleend geregistreerd en mag je het meenemen.'
              : 'Dit boek is momenteel uitgeleend. Reserveer en je krijgt bericht zodra het klaarligt.'}
          </p>

          {submitted ? (
            <div className="mt-4 rounded-xl bg-emerald-50 text-emerald-800 p-4 text-sm">
              <div className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /><div>{submitted.type === 'loan' ? <><p className="font-semibold">Je kunt het boek nu meenemen.</p><p className="mt-1">De uitlening is geregistreerd. Lever het boek uiterlijk <strong>{submitted.due_date}</strong> weer in bij de boekenkast.</p></> : <><p className="font-semibold">Je reservering is ontvangen.</p><p className="mt-1">Je krijgt bericht zodra het boek beschikbaar is.</p></>}</div></div>
            </div>
          ) : (
            <form onSubmit={submitRequest} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input value={formData.requester_name} onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })} className="rounded-xl" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>E-mail *</Label>
                  <Input type="email" value={formData.requester_email} onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })} className="rounded-xl" />
                </div>
                {book.available ? <div className="space-y-2"><Label>Hoe lang wil je lenen?</Label><select value={formData.loan_days} onChange={(e) => setFormData({ ...formData, loan_days: e.target.value })} className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"><option value="7">7 dagen</option><option value="14">14 dagen</option><option value="21">21 dagen</option><option value="30">30 dagen (maximaal)</option></select></div> : <div className="space-y-2"><Label>Telefoon</Label><Input value={formData.requester_phone} onChange={(e) => setFormData({ ...formData, requester_phone: e.target.value })} className="rounded-xl" /></div>}
              </div>
              <div className="space-y-2">
                <Label>Opmerking</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                {submitting ? 'Registreren...' : (book.available ? <><Clock3 className="mr-2 h-4 w-4" />Registreer en neem mee</> : 'Reserveer dit boek')}
              </Button>
            </form>
          )}
        </div>
        )}

        {similarBooks.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Vergelijkbare boeken</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {similarBooks.map((similar) => <SimilarBookCard key={similar.id} book={similar} orgSlug={org.slug} />)}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LibraryPublicBookPage;
